import { AnimationMixer, LoopOnce, LoopRepeat } from 'three';

const DEFAULT_FADE = 0.2;

/**
 * Owns one fighter's AnimationMixer and its clip library. `play(name)`
 * crossfades from whatever is running, so callers never touch actions or
 * weights — they name a clip and the animator handles the blend.
 */
export function createAnimator(root, clips = []) {
  const mixer = new AnimationMixer(root);
  const actions = new Map();
  let current = null;
  let currentName = null;
  const finishListeners = new Set();

  mixer.addEventListener('finished', (event) => {
    const name = nameOf(event.action);
    for (const listener of finishListeners) listener(name);
  });

  function nameOf(action) {
    for (const [name, candidate] of actions) if (candidate === action) return name;
    return null;
  }

  function add(clip) {
    if (!clip?.tracks?.length) return false;
    // A later clip of the same name replaces the earlier one, so an animation
    // pack can override a clip that shipped inside the model.
    actions.get(clip.name)?.stop();
    actions.set(clip.name, mixer.clipAction(clip));
    return true;
  }

  for (const clip of clips) add(clip);

  return {
    mixer,

    add,
    addClips(list) {
      return list.filter(add).length;
    },

    has: (name) => actions.has(name),
    names: () => [...actions.keys()],
    get current() {
      return currentName;
    },

    /** Unscaled length of a clip in seconds, or null if it is not loaded. */
    duration: (name) => actions.get(name)?.getClip().duration ?? null,

    /**
     * Rescales whatever is playing. The strike layer uses this to run one
     * placeholder punch clip at two speeds, and it has to go through the
     * action rather than through play() so the crossfade already in flight is
     * not thrown away and restarted.
     */
    setTimeScale(value) {
      current?.setEffectiveTimeScale(value);
      return currentName;
    },

    /**
     * Crossfades to `name`. Returns false for an unknown clip rather than
     * throwing, so a missing animation degrades to "keeps doing what it was
     * doing" instead of taking the frame down.
     */
    play(name, { fade = DEFAULT_FADE, loop = true, timeScale = 1, restart = false } = {}) {
      const next = actions.get(name);
      if (!next) return false;
      if (next === current && !restart) return true;

      next.reset();
      next.setLoop(loop ? LoopRepeat : LoopOnce, loop ? Infinity : 1);
      next.clampWhenFinished = !loop;
      next.setEffectiveTimeScale(timeScale);
      next.setEffectiveWeight(1);
      next.fadeIn(fade).play();

      if (current && current !== next) current.fadeOut(fade);

      current = next;
      currentName = name;
      return true;
    },

    onFinished(listener) {
      finishListeners.add(listener);
      return () => finishListeners.delete(listener);
    },

    update(dt) {
      mixer.update(dt);
    },

    dispose() {
      mixer.stopAllAction();
      mixer.uncacheRoot(root);
      actions.clear();
      finishListeners.clear();
      current = null;
      currentName = null;
    },
  };
}
